import { linePalette } from "@/lib/palette";
import type { Line } from "@/lib/schemas/lines";
import type { ProductDetail, ProductListItem } from "@/lib/schemas/products";

// DropReel only needs slug + name + subject — accept either list or detail
// shape so this component is the same on the feed, line landing, and PDP.
type DropReelProduct = Pick<
	ProductListItem | ProductDetail,
	"slug" | "name" | "subject"
>;

type DropReelProps = {
	line: Line;
	product: DropReelProduct;
	height: number;
	showLabel?: boolean;
};

// Unique-enough ID for the SVG filter per render (not reactive — server-safe).
function filterId(slug: string) {
	return `noise-${slug.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`;
}

// Short hand for the reel's "front print tease" — derive a 3–5 char tag
// from the product name. Display-only.
function shortCode(p: DropReelProduct): string {
	const source = p.name.trim();
	const word = source.split(/[\s-]/)[0] ?? source;
	return word.slice(0, 5).toUpperCase();
}

// Static SVG composition — one frame of a three-scene drop reel.
// Uses a div + styled children (easier than a full <svg>) with an inline
// SVG feTurbulence filter for the grain texture. No animations, no video.
export function DropReel({
	line,
	product,
	height,
	showLabel = true,
}: DropReelProps) {
	const id = filterId(product.slug);
	const code = shortCode(product);
	const { paper, ink, accent } = linePalette(line);

	return (
		<div
			className="relative select-none overflow-hidden"
			style={{ height, backgroundColor: paper }}
			aria-label={`Drop reel — ${product.name} · ${line.name}`}
			role="img"
		>
			{/* Grain texture — inline SVG feTurbulence, no external assets */}
			<svg
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 h-full w-full"
				style={{ opacity: 0.1 }}
			>
				<defs>
					<filter id={id}>
						<feTurbulence
							type="fractalNoise"
							baseFrequency="0.65"
							numOctaves="3"
							stitchTiles="stitch"
						/>
						<feColorMatrix type="saturate" values="0" />
						<feBlend in="SourceGraphic" mode="multiply" />
					</filter>
				</defs>
				<rect width="100%" height="100%" filter={`url(#${id})`} />
			</svg>

			{/* Shirt silhouette — centred block, ~60% wide × 70% tall */}
			<div
				className="absolute flex items-center justify-center"
				style={{
					left: "20%",
					right: "20%",
					top: "15%",
					bottom: "15%",
					backgroundColor: ink,
					// accent at 30% opacity as inner border tint
					outline: `1px solid ${accent}4D`,
					outlineOffset: "-1px",
				}}
			>
				{/* Short tag — "front print" tease */}
				<span
					className="font-mono text-[2rem] tracking-widest"
					style={{ color: paper }}
				>
					{code}
				</span>
			</div>

			{/* Top-left chrome: recording dot + label */}
			{showLabel && (
				<div
					className="absolute left-3 top-3 flex items-center gap-2"
					aria-hidden="true"
				>
					{/* #ea2b2b — conventional "recording" red, distinct from site accent */}
					<span
						className="block rounded-full"
						style={{
							width: 6,
							height: 6,
							backgroundColor: "#ea2b2b",
						}}
					/>
					<span
						className="font-mono text-[9px] tracking-widest"
						style={{ color: ink, opacity: 0.7 }}
					>
						DROP REEL · 0:08 LOOP
					</span>
				</div>
			)}

			{/* Bottom scrubber — 3 equal segments, first filled */}
			<div
				className="absolute bottom-4 left-3 right-3 flex gap-1"
				aria-hidden="true"
			>
				{[0, 1, 2].map((i) => (
					<div
						key={i}
						className="h-[2px] flex-1"
						style={{
							backgroundColor: ink,
							opacity: i === 0 ? 0.6 : 0.18,
						}}
					/>
				))}
			</div>
		</div>
	);
}
